import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, CircleSlash } from "lucide-react";
import { Team } from "../../types";
import { useState, useCallback, memo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface TeamSelectorProps {
  teams: Team[];
  guardTeams: Team[];
  onTeamAction: (teamId: string) => void;
}

export const TeamSelector = memo(({ teams, guardTeams, onTeamAction }: TeamSelectorProps) => {
  const [pendingTeamId, setPendingTeamId] = useState<string | null>(null);
  const [selectKey, setSelectKey] = useState(0);

  const handleTeamSelect = useCallback((value: string) => {
    setPendingTeamId(value);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (pendingTeamId) {
      await onTeamAction(pendingTeamId);
      setPendingTeamId(null);
      setSelectKey(prev => prev + 1);
    }
  }, [pendingTeamId, onTeamAction]);

  const handleCancel = useCallback(() => {
    setPendingTeamId(null);
    setSelectKey(prev => prev + 1);
  }, []);

  const getActionText = useCallback((teamId: string) => {
    const isAssigned = guardTeams.some(gt => gt.id === teamId);
    const team = teams.find(t => t.id === teamId);
    return isAssigned ? `Remove from ${team?.name}` : `Add to ${team?.name}`;
  }, [teams, guardTeams]);

  const handleSelectClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDialogClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <div className="mt-4">
      <Label htmlFor="manage-team">Manage Teams</Label>
      <div className="flex items-center gap-2 mt-1">
        <Dialog open={!!pendingTeamId} onOpenChange={(open) => !open && handleCancel()}>
          <div onClick={handleSelectClick} onMouseDown={handleSelectClick} className="w-full">
            <Select 
              key={selectKey}
              value={pendingTeamId || undefined} 
              onValueChange={handleTeamSelect}
            >
              <SelectTrigger id="manage-team" className="w-full bg-white">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent 
                className="bg-white max-h-[40vh] overflow-y-auto z-50"
                position="popper"
                side="bottom"
                align="start"
                sideOffset={8}
              >
                {teams.map((team) => {
                  const isAssigned = guardTeams.some(gt => gt.id === team.id);
                  return (
                    <SelectItem 
                      key={team.id} 
                      value={team.id}
                      className="relative pl-8 cursor-pointer truncate pr-2"
                    >
                      <span className="absolute left-2 top-1/2 -translate-y-1/2">
                        {isAssigned ? (
                          <Check className="h-4 w-4 text-primary" />
                        ) : (
                          <CircleSlash className="h-4 w-4 text-muted-foreground opacity-40" />
                        )}
                      </span>
                      <span className="truncate block max-w-[200px]" title={team.name}>
                        {team.name}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <DialogContent onClick={handleDialogClick}>
            <DialogHeader>
              <DialogTitle>Confirm Team Action</DialogTitle>
              <DialogDescription>
                {pendingTeamId && `Are you sure you want to ${getActionText(pendingTeamId)}?`}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleConfirm}>
                Confirm
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
});

TeamSelector.displayName = 'TeamSelector';
